import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Heart, MessageSquare, Microscope, ChevronLeft, Pin, Star, Aperture, Eye, Share2 } from "lucide-react";
import { api, resolveImageUrl, formatApiError } from "../lib/api";
import { authorHref } from "../lib/routes";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import ExifPlaque from "../components/ExifPlaque";
import VerifiedDot from "../components/VerifiedDot";
import ShareMenu from "../components/ShareMenu";
import EditPhotoDialog from "../components/EditPhotoDialog";
import DarkroomViewer from "../components/DarkroomViewer";
import { localizedTitle, localizedDescription, needsTranslation } from "../lib/photoI18n";
import { categoryLabel } from "../lib/categories";

const CRITIQUE_AXES = [
  { key: "composition_rating", label_key: "critique_composition" },
  { key: "exposure_rating", label_key: "critique_exposure" },
  { key: "processing_rating", label_key: "critique_processing" },
];

function StarPicker({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(value === n ? null : n)}
          className={`p-0.5 transition-colors ${
            value && n <= value ? "text-sienna" : "text-zinc-600 hover:text-zinc-400"
          }`}
          data-testid={`${testid}-star-${n}`}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          <Star size={14} fill={value && n <= value ? "currentColor" : "none"} />
        </button>
      ))}
      <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 w-6">
        {value ? `${value}/5` : "—"}
      </span>
    </div>
  );
}

function RatingBadge({ label, value }) {
  if (!value) return null;
  return (
    <div className="inline-flex items-center gap-1.5 border border-zinc-800 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
      <span>{label}</span>
      <span className="flex items-center gap-0.5 text-sienna">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} size={9} fill={n <= value ? "currentColor" : "none"} className={n <= value ? "text-sienna" : "text-zinc-700"} />
        ))}
      </span>
    </div>
  );
}

function CommentBlock({ items, kind, onPost, t, user }) {
  const [draft, setDraft] = useState("");
  const [ratings, setRatings] = useState({ composition_rating: null, exposure_rating: null, processing_rating: null });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const filtered = items.filter((c) => c.kind === kind);

  const submit = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    setErr("");
    try {
      const extra = kind === "critique" ? ratings : {};
      await onPost(draft.trim(), kind, extra);
      setDraft("");
      setRatings({ composition_rating: null, exposure_rating: null, processing_rating: null });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`comments-${kind}`}>
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 border-b border-zinc-900 pb-3 mb-5">
        {kind === "critique" ? <Microscope size={12} /> : <MessageSquare size={12} />}
        {kind === "critique" ? t("critique") : t("comments")}
        <span className="ml-auto text-zinc-600">{filtered.length}</span>
      </div>
      <div className="space-y-6">
        {filtered.map((c) => (
          <div key={c.id} className="border-l border-zinc-800 pl-4" data-testid={`comment-${c.id}`}>
            <div className="font-serif text-zinc-100 font-light">{c.user_name}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-0.5">
              {new Date(c.created_at).toLocaleDateString()}
            </div>
            {kind === "critique" &&
              (c.composition_rating || c.exposure_rating || c.processing_rating) && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid={`critique-ratings-${c.id}`}>
                  <RatingBadge label={t("critique_composition")} value={c.composition_rating} />
                  <RatingBadge label={t("critique_exposure")} value={c.exposure_rating} />
                  <RatingBadge label={t("critique_processing")} value={c.processing_rating} />
                </div>
              )}
            <div className="mt-2 text-sm text-zinc-300 font-light leading-relaxed whitespace-pre-wrap">
              {c.content}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-zinc-600 font-mono text-[11px] uppercase tracking-[0.25em]">—</div>
        )}
      </div>

      {user && user !== false ? (
        <div className="mt-6">
          {kind === "critique" && (
            <div className="space-y-3 mb-4" data-testid="critique-rating-grid">
              {CRITIQUE_AXES.map((axis) => (
                <div
                  key={axis.key}
                  className="flex items-center justify-between border border-zinc-900 px-4 py-2.5"
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">
                    {t(axis.label_key)}
                  </span>
                  <StarPicker
                    value={ratings[axis.key]}
                    onChange={(v) => setRatings((r) => ({ ...r, [axis.key]: v }))}
                    testid={`critique-${axis.key}`}
                  />
                </div>
              ))}
            </div>
          )}
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={kind === "critique" ? 4 : 2}
            placeholder={kind === "critique" ? t("critique_notes") : t("write_comment")}
            className="w-full bg-transparent border border-zinc-800 px-4 py-3 text-zinc-100 font-light text-sm focus:outline-none focus:border-zinc-500 resize-none"
            data-testid={`comment-input-${kind}`}
          />
          {err && <div className="text-red-400 text-xs mt-2">{err}</div>}
          <button
            onClick={submit}
            disabled={busy || !draft.trim()}
            className="mt-3 px-5 py-2 bg-sienna text-white text-xs font-light tracking-wide uppercase hover:bg-sienna-400 disabled:opacity-50 transition-colors"
            data-testid={`comment-submit-${kind}`}
          >
            {busy ? t("loading") : t("post")}
          </button>
        </div>
      ) : (
        <div className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
          <Link to="/login" className="underline underline-offset-4 hover:text-zinc-200">
            {t("sign_in")}
          </Link>{" "}
          →
        </div>
      )}
    </div>
  );
}

export default function PhotoDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [photo, setPhoto] = useState(null);
  const [comments, setComments] = useState([]);
  const [err, setErr] = useState("");
  // Bumped whenever the photo is edited so the <img> src busts the CDN cache.
  const [imgVersion, setImgVersion] = useState(0);
  // When set, the description block renders in the OTHER language than the
  // viewer's UI (the "View in English / Ver en español" ghost toggle).
  const [altLang, setAltLang] = useState(null);
  const darkroom = params.get("darkroom") === "1";

  const enterDarkroom = () => {
    const next = new URLSearchParams(params);
    next.set("darkroom", "1");
    setParams(next, { replace: false });
  };
  const exitDarkroom = () => {
    const next = new URLSearchParams(params);
    next.delete("darkroom");
    setParams(next, { replace: true });
  };

  const loadPhoto = async () => {
    const { data } = await api.get(`/photos/${id}`);
    setPhoto(data);
  };
  const loadComments = async () => {
    const { data } = await api.get(`/photos/${id}/comments`);
    setComments(data);
  };

  useEffect(() => {
    loadPhoto().catch((e) => setErr(formatApiError(e.response?.data?.detail) || e.message));
    loadComments().catch(() => {});
  }, [id]);

  // Clear the "View in other language" override whenever the photo or UI lang changes.
  useEffect(() => { setAltLang(null); }, [id, lang]);

  // On-demand translation: when the viewer's UI language doesn't have a
  // cached translation for this photo, fire the backend endpoint and re-fetch.
  // Fire-and-forget — original title/description shows in the meantime.
  useEffect(() => {
    if (!photo || !needsTranslation(photo, lang)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/photos/${photo.id}/translate?target=${lang}`);
        if (!cancelled) setPhoto(data);
      } catch { /* silent — fallback to canonical text */ }
    })();
    return () => { cancelled = true; };
  }, [photo, lang]);

  // Best-effort: keep document.title + meta description in sync so JS-aware
  // scrapers and browser history reflect the current frame.
  useEffect(() => {
    if (!photo) return;
    const lTitle = localizedTitle(photo, lang);
    const lDesc = localizedDescription(photo, lang);
    const prevTitle = document.title;
    document.title = `${lTitle} — by ${photo.user_name} · Social Gallery`;
    const setMeta = (name, attr, value) => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    const desc = (lDesc || `A photograph by ${photo.user_name} on Social Gallery.`).slice(0, 200);
    setMeta("description", "name", desc);
    setMeta("og:title", "property", `${lTitle} — by ${photo.user_name}`);
    setMeta("og:description", "property", desc);
    setMeta("og:image", "property", `${process.env.REACT_APP_BACKEND_URL}/api/photos/${photo.id}/og.png`);
    setMeta("twitter:title", "name", `${lTitle} — by ${photo.user_name}`);
    setMeta("twitter:image", "name", `${process.env.REACT_APP_BACKEND_URL}/api/photos/${photo.id}/og.png`);
    return () => { document.title = prevTitle; };
  }, [photo, lang]);

  const appreciate = async () => {
    if (!user || user === false) return;
    const prev = photo;
    setPhoto({
      ...photo,
      has_appreciated: !photo.has_appreciated,
      appreciations: photo.appreciations + (photo.has_appreciated ? -1 : 1),
    });
    try {
      const { data } = await api.post(`/photos/${id}/appreciate`);
      setPhoto((p) => ({ ...p, has_appreciated: data.has_appreciated, appreciations: data.appreciations }));
    } catch {
      setPhoto(prev);
    }
  };

  const postComment = async (content, kind, extra = {}) => {
    await api.post(`/photos/${id}/comments`, { content, kind, ...extra });
    await loadComments();
    const { data } = await api.get(`/photos/${id}`);
    setPhoto(data);
  };

  const togglePick = async () => {
    if (!user || user.role !== "admin") return;
    try {
      const { data } = await api.post(`/photos/${id}/pick`, { pick: !photo.is_pick });
      setPhoto(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (err) {
    return <div className="p-10 font-mono text-sm text-red-400">{err}</div>;
  }
  if (!photo) {
    return (
      <div className="p-10 font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
        {t("loading")}
      </div>
    );
  }

  return (
    <div data-testid="photo-detail-page">
      {darkroom && photo && (
        <DarkroomViewer photo={photo} onExit={exitDarkroom} />
      )}
      <div className="mx-auto max-w-[1600px] px-6 sm:px-10 pt-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-zinc-200"
          data-testid="back-to-feed-link"
        >
          <ChevronLeft size={14} /> {t("back_to_feed")}
        </Link>
      </div>

      <div className="mx-auto max-w-[1600px] px-6 sm:px-10 pt-8 pb-20 grid gap-10 lg:gap-14 lg:grid-cols-12 lg:items-start">
        <div className="lg:col-span-8 bg-zinc-900 border border-zinc-900 relative group">
          <img
            src={`${resolveImageUrl(photo.image_url)}${imgVersion ? (photo.image_url.includes("?") ? "&" : "?") + "v=" + imgVersion : ""}`}
            alt={photo.title}
            className="w-full h-auto block"
            data-testid="photo-detail-image"
          />
          <button
            onClick={enterDarkroom}
            className="absolute top-3 right-3 inline-flex items-center gap-2 bg-black/70 hover:bg-black/90 text-zinc-100 border border-white/15 hover:border-sienna px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid="darkroom-enter-button"
            title={t("darkroom_hint")}
          >
            <Aperture size={12} />
            {t("darkroom_mode")}
          </button>
        </div>

        <aside className="lg:col-span-4 lg:border-l lg:border-zinc-900 lg:pl-10 space-y-10">
          <div>
            {photo.is_pick && (
              <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-100 border border-zinc-700 px-3 py-1.5 mb-4" data-testid="photo-pick-badge">
                <Pin size={10} /> {t("pick_of_week")}
              </div>
            )}
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              {t("by_artist")}
            </div>
            <Link
              to={authorHref(photo)}
              className="mt-1 inline-flex items-center gap-2 font-serif text-2xl text-zinc-50 font-light hover:underline underline-offset-4"
              data-testid="photo-author-link"
            >
              {photo.user_name}
              <VerifiedDot visible={photo.user_is_premium} size={9} />
            </Link>
            <h1
              className="mt-6 font-serif text-4xl text-zinc-50 font-light tracking-tight leading-[1.05]"
              data-testid="photo-detail-title"
            >
              {localizedTitle(photo, altLang || lang)}
            </h1>
            {localizedDescription(photo, altLang || lang) && (
              <p
                className="mt-4 text-zinc-400 font-light leading-relaxed"
                data-testid="photo-detail-description"
              >
                {localizedDescription(photo, altLang || lang)}
              </p>
            )}
            {photo.category && (
              <Link
                to={`/category/${photo.category}`}
                className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 border border-zinc-800 text-zinc-400 hover:border-sienna hover:text-sienna font-mono text-[10px] uppercase tracking-[0.3em] transition-colors"
                data-testid="photo-detail-category-pill"
              >
                {categoryLabel(photo.category, lang)}
              </Link>
            )}
            {(() => {
              const other = lang === "en" ? "es" : "en";
              const otherTitle = (photo.title_i18n && photo.title_i18n[other]) || "";
              const otherDesc = (photo.description_i18n && photo.description_i18n[other]) || "";
              // Show the toggle only if we have actual alt-lang content cached
              // OR we have source text (clicking will lazily translate).
              const hasAlt = otherTitle.trim() || otherDesc.trim() || photo.title || photo.description;
              if (!hasAlt) return null;
              const showingAlt = altLang === other;
              const handleToggle = async () => {
                if (showingAlt) {
                  setAltLang(null);
                  return;
                }
                // If alt-lang not cached yet, trigger backend translate.
                if (!otherTitle.trim() && !otherDesc.trim()) {
                  try {
                    const { data } = await api.post(`/photos/${photo.id}/translate?target=${other}`);
                    setPhoto(data);
                  } catch { /* graceful: still flip and let fallback render canonical text */ }
                }
                setAltLang(other);
              };
              return (
                <button
                  onClick={handleToggle}
                  className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-sienna transition-colors underline underline-offset-4 decoration-zinc-700 hover:decoration-sienna"
                  data-testid="view-alt-lang-button"
                  title={showingAlt ? t("showing_alt_lang") : ""}
                >
                  {other === "en" ? t("view_in_english") : t("view_in_spanish")}
                </button>
              );
            })()}
          </div>

          <button
            onClick={appreciate}
            disabled={!user || user === false}
            className={`w-full inline-flex items-center justify-between border px-5 py-4 transition-colors ${
              photo.has_appreciated
                ? "bg-sienna text-white border-sienna hover:bg-sienna-400"
                : "border-zinc-800 text-zinc-100 hover:bg-zinc-900 hover:border-zinc-700"
            }`}
            data-testid="appreciate-button"
          >
            <span className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em]">
              <Heart size={14} fill={photo.has_appreciated ? "currentColor" : "none"} />
              {photo.has_appreciated ? t("appreciated") : t("appreciate")}
            </span>
            <span className="font-mono text-sm">{photo.appreciations}</span>
          </button>

          <ShareMenu
            url={`${process.env.REACT_APP_BACKEND_URL}/api/photos/${id}/share`}
            title={`${photo.title} — by ${photo.user_name}`}
            testid="share"
            className="w-full justify-between !px-5 !py-4 !text-[11px]"
            onShare={async () => {
              // Optimistic bump; fire-and-forget server tick.
              setPhoto((p) => p ? { ...p, shares: (p.shares ?? 0) + 1 } : p);
              try { await api.post(`/photos/${id}/track-share`); } catch { /* ignore */ }
            }}
          />

          <div
            className="grid grid-cols-2 gap-3 border-y border-zinc-900 py-4"
            data-testid="photo-stats"
          >
            <div className="flex items-center gap-3">
              <Eye size={14} className="text-zinc-500 shrink-0" />
              <div>
                <div
                  className="font-serif text-xl text-zinc-100 font-light leading-none"
                  data-testid="photo-views-count"
                >
                  {photo.views ?? 0}
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  {t("stats_views")}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Share2 size={14} className="text-zinc-500 shrink-0" />
              <div>
                <div
                  className="font-serif text-xl text-zinc-100 font-light leading-none"
                  data-testid="photo-shares-count"
                >
                  {photo.shares ?? 0}
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  {t("stats_shares")}
                </div>
              </div>
            </div>
          </div>

          {user && user !== false && user.id === photo.user_id && (
            <EditPhotoDialog
              photo={photo}
              onUpdated={(updated) => {
                setPhoto(updated);
                setImgVersion((v) => v + 1);
              }}
            />
          )}

          {user && user.role === "admin" && (
            <button
              onClick={togglePick}
              className={`w-full inline-flex items-center justify-between border px-5 py-4 transition-colors ${
                photo.is_pick
                  ? "border-zinc-700 text-zinc-100 bg-zinc-900"
                  : "border-zinc-800 text-zinc-300 hover:bg-zinc-900 hover:border-zinc-700"
              }`}
              data-testid="pin-pick-button"
            >
              <span className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em]">
                <Pin size={12} fill={photo.is_pick ? "currentColor" : "none"} />
                {photo.is_pick ? t("pinned") : t("pin_as_pick")}
              </span>
            </button>
          )}

          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 border-b border-zinc-900 pb-3 mb-4">
              {t("shot_details")}
            </div>
            <ExifPlaque exif={photo.exif || {}} />
            {photo.hub_slug && (
              <Link
                to={`/c/${photo.hub_slug}`}
                className="mt-3 inline-flex items-center gap-2 border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400 hover:text-sienna hover:border-sienna transition-colors"
                data-testid="photo-hub-link"
              >
                <span className="text-zinc-600">/c/</span>
                {photo.hub_slug}
              </Link>
            )}
          </div>

          <CommentBlock items={comments} kind="critique" onPost={postComment} t={t} user={user} />
          <CommentBlock items={comments} kind="comment" onPost={postComment} t={t} user={user} />
        </aside>
      </div>
    </div>
  );
}
